import React, { useState, useEffect, useRef } from 'react';
import {Box,Card,CardContent,TextField,Button,Typography,InputAdornment,IconButton,Fade,Grow,CircularProgress,Container,Paper, useTheme} from '@mui/material';
import {Visibility,VisibilityOff,Email,Lock,Login as LoginIcon} from '@mui/icons-material';
import { setUser } from '../../store/slices/userSlice';
import { useDispatch } from 'react-redux';
import { POST } from '../../configs/api';
import { api } from '../../utils/constants';

function Login() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({email: '',password: ''});
  const [showError, setShowError] = useState('');
  const dispatch = useDispatch();

  // Refs for debouncing
  const emailTimeoutRef = useRef(null);
  const passwordTimeoutRef = useRef(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 3;
  };

  // Debounced validation functions
  const debouncedEmailValidation = (value) => {
    if (emailTimeoutRef.current) {
      clearTimeout(emailTimeoutRef.current);
    }

    emailTimeoutRef.current = setTimeout(() => {
      if (value && !validateEmail(value)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address.' }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }, 1500); // Wait 1.5 seconds before showing error
  };

  const debouncedPasswordValidation = (value) => {
    if (passwordTimeoutRef.current) {
      clearTimeout(passwordTimeoutRef.current);
    }

    passwordTimeoutRef.current = setTimeout(() => {
      if (value && !validatePassword(value)) {
        setErrors(prev => ({ ...prev, password: 'Password must be at least 3 characters.' }));
      } else {
        setErrors(prev => ({ ...prev, password: '' }));
      }
    }, 1500); // Wait 1.5 seconds before showing error
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear any existing error immediately when user starts typing
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: '' }));
    }
    
    // Only validate if user has interacted with the field
    if (value) {
      debouncedEmailValidation(value);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    
    // Clear any existing error immediately when user starts typing
    if (errors.password) {
      setErrors(prev => ({ ...prev, password: '' }));
    }
    
    // Only validate if user has interacted with the field
    if (value) {
      debouncedPasswordValidation(value);
    }
  };

  const handleEmailBlur = () => {
    // Immediate validation on blur
    if (email && !validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address.' }));
    }
  };

  const handlePasswordBlur = () => {
    // Immediate validation on blur
    if (password && !validatePassword(password)) {
      setErrors(prev => ({ ...prev, password: 'Password must be at least 3 characters.' }));
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
      if (passwordTimeoutRef.current) {
        clearTimeout(passwordTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate all fields
    const emailValid = validateEmail(email);
    const passwordValid = validatePassword(password);
    
    setErrors({email: !emailValid ? 'Please enter a valid email address.' : '',password: !passwordValid ? 'Password must be at least 3 characters.' : ''});

    if (emailValid && passwordValid) {
      const {data, status, message} = await POST(api.login, {}, { email, password });
      if (status === 200) {
        dispatch(setUser({...data.user, accessToken: data.accessToken}));
      } else if (status === 401) {
        setShowError(message || 'Invalid email or password.');
      } else {
        setShowError(message || 'Something went wrong, please try again later.');
      }
    }
    setLoading(false);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #0f0f0f 0%, rgb(130, 130, 130) 100%)',
          py: 3
        }}
      >
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              background: isDarkMode 
                ? 'rgba(30, 41, 59, 0.95)'
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              width: '100%',
              maxWidth: 440,
              border: isDarkMode ? '1px solid #334155' : 'none',
            }}
          >
            <Card sx={{ boxShadow: 'none', background: 'transparent' }}>
              <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                <Grow in timeout={1000}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: isDarkMode
                          ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                          : 'linear-gradient(135deg, #0f0f0f 0%, rgb(130, 130, 130) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px auto',
                        boxShadow: isDarkMode
                          ? '0 8px 32px rgba(59, 130, 246, 0.4)'
                          : '0 8px 32px rgba(102, 126, 234, 0.3)',
                        animation: 'pulse 2s infinite'
                      }}
                    >
                      <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h4"
                      component="h1"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      Welcome Back
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Sign in to continue to your account
                    </Typography>
                  </Box>
                </Grow>

                <Fade in timeout={1200}>
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      error={!!errors.email}
                      helperText={errors.email}
                      margin="normal"
                      required
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color={errors.email ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                          transition: 'all 0.3s ease-in-out',
                          '& fieldset': {
                            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#475569' : '#000000',
                            borderWidth: '1px',
                          },
                          '&:hover': {
                            boxShadow: isDarkMode 
                              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                              : '0 4px 20px rgba(0,0,0,0.1)',
                          },
                          '&.Mui-focused': {
                            boxShadow: isDarkMode
                              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                              : '0 4px 20px rgba(0, 0, 0, 0.15)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'text.secondary',
                        },
                        '& .MuiInputBase-input': {
                          color: 'text.primary',
                        },
                        '& .MuiInputBase-input::selection': {
                          backgroundColor: isDarkMode ? '#475569' : '#e3f2fd',
                        }
                      }}
                    />

                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={handlePasswordChange}
                      onBlur={handlePasswordBlur}
                      error={!!errors.password}
                      helperText={errors.password}
                      margin="normal"
                      required
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color={errors.password ? 'error' : 'action'} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              disabled={loading}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                          transition: 'all 0.3s ease-in-out',
                          '& fieldset': {
                            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                          },
                          '&:hover fieldset': {
                            borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: isDarkMode ? '#475569' : '#000000',
                            borderWidth: '1px',
                          },
                          '&:hover': {
                            boxShadow: isDarkMode 
                              ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                              : '0 4px 20px rgba(0,0,0,0.1)',
                          },
                          '&.Mui-focused': {
                            boxShadow: isDarkMode
                              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                              : '0 4px 20px rgba(0, 0, 0, 0.15)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: 'text.secondary',
                        },
                        '& .MuiInputBase-input': {
                          color: 'text.primary',
                        },
                        '& .MuiInputBase-input::selection': {
                          backgroundColor: isDarkMode ? '#475569' : '#e3f2fd',
                        }
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading || !email || !password}
                      loading={loading}
                      loadingPosition="start"
                      startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        borderRadius: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        background: isDarkMode
                          ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                          : 'linear-gradient(135deg, #0f0f0f 0%, rgb(130, 130, 130) 100%)',
                        boxShadow: isDarkMode
                          ? '0 8px 32px rgba(59, 130, 246, 0.4)'
                          : '0 8px 32px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: isDarkMode
                            ? '0 12px 40px rgba(59, 130, 246, 0.5)'
                            : '0 12px 40px rgba(102, 126, 234, 0.4)',
                          background: isDarkMode
                            ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                            : 'linear-gradient(135deg, #1a1a1a 0%, rgb(110, 110, 110) 100%)',
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                        '&.Mui-disabled': {
                          background: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : 'rgba(0, 0, 0, 0.12)',
                          color: isDarkMode ? 'rgba(203, 213, 225, 0.5)' : 'rgba(0, 0, 0, 0.26)',
                        }
                      }}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                    {showError && (
                      <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                        {showError}
                      </Typography>
                    )}
                  </Box>
                </Fade>
              </CardContent>
            </Card>
          </Paper>
        </Fade>
      </Box>

      {/* Add keyframe animations */}
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(1);
              box-shadow: ${isDarkMode 
                ? '0 8px 32px rgba(59, 130, 246, 0.4)'
                : '0 8px 32px rgba(102, 126, 234, 0.3)'};
            }
            50% {
              transform: scale(1.05);
              box-shadow: ${isDarkMode
                ? '0 12px 40px rgba(59, 130, 246, 0.6)'
                : '0 12px 40px rgba(130, 130, 130, 0.5)'};
            }
            100% {
              transform: scale(1);
              box-shadow: ${isDarkMode
                ? '0 8px 32px rgba(59, 130, 246, 0.4)'
                : '0 8px 32px rgba(102, 126, 234, 0.3)'};
            }
          }
        `}
      </style>
    </Container>
  );
}

export default Login;